import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner } from "react-bootstrap";
import { getMensagens, pegarConfiguracao, estaConectado, verificarClinicaCadastrada } from '../data/api/gsConnectApi';
import WelcomePage from './WelcomePage';
import { FormatarStrDateParaDDMMYYYYHHMM } from '../utils/utils';
import { Link } from "react-router-dom";

function DashboardView() {
  const [stats, setStats] = useState({ enviosHoje: 0, mensagensEnviadas: 0, automacoesAtivas: 5, instanciasConectadas: 2, loading: true });
  const [recentActivity, setRecentActivity] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartStats, setChartStats] = useState({ total: 0, average: 0, peak: 0, today: 0 });
  const [clinicaCadastrada, setClinicaCadastrada] = useState(null);
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    verificarECarregarDados();
  }, []);

  const verificarECarregarDados = async () => {
    try {
      const cadastrada = await verificarClinicaCadastrada();
      setClinicaCadastrada(cadastrada);
      
      if (cadastrada) {
        loadDashboardData();
        const interval = setInterval(loadDashboardData, 30000);
        return () => clearInterval(interval);
      }
    } catch (error) {
      console.error('Erro ao verificar clínica:', error);
      setClinicaCadastrada(false);
    }
  };

  useEffect(() => {
    if (chartData.length > 0 && chartRef.current) drawChart();
  }, [chartData]);

  const loadDashboardData = async () => {
    try {
      const hoje = new Date();
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
      
      // Buscar dados de mensagens
      const [mensagensMes, mensagensHoje] = await Promise.all([
        getMensagens(undefined, inicioMes, hoje, undefined, undefined),
        getMensagens(undefined, inicioHoje, hoje, undefined, undefined)
      ]);

      // Calcular automações ativas
      const automacoesAtivas = await calcularAutomacoesAtivas();
      
      // Calcular instâncias conectadas
      const instanciasConectadas = await calcularInstanciasConectadas();

      const dadosGrafico = processarDadosGrafico(mensagensMes || [], hoje);
      const totalMes = Array.isArray(mensagensMes) ? mensagensMes.length : 0;
      const totalHoje = Array.isArray(mensagensHoje) ? mensagensHoje.length : 0;

      setStats({ 
        enviosHoje: totalHoje, 
        mensagensEnviadas: totalMes, 
        automacoesAtivas, 
        instanciasConectadas, 
        loading: false 
      });
      setChartData(dadosGrafico);
      setRecentActivity(processarAtividadesRecentes(mensagensHoje || []));
      calculateChartStats(dadosGrafico);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const calcularAutomacoesAtivas = async () => {
    try {
      // Lista de configurações de automação para verificar
      const tiposAutomacao = [
        'AUTOMACAO_ANIVERSARIANTES',
        'AUTOMACAO_CONFIRMACAO_AGENDAMENTO',
        'AUTOMACAO_LEMBRETE_AGENDAMENTO',
        'AUTOMACAO_PRE_CADASTRO_AGENDAMENTO'
      ];

      let totalAtivas = 0;

      for (const tipo of tiposAutomacao) {
        try {
          const config = await pegarConfiguracao(tipo);
          
          // Só conta como ativa se explicitamente for 'true'
          if (config && config.valor === 'true') {
            totalAtivas++;
          }
        } catch (error) {
          console.warn(`Erro ao verificar automação ${tipo}:`, error);
        }
      }

      return totalAtivas;
    } catch (error) {
      console.error('Erro ao calcular automações ativas:', error);
      return 0;
    }
  };

  const calcularInstanciasConectadas = async () => {
    try {
      // Verificar instâncias 1, 2, 3 e 4
      const instancias = [1, 2, 3, 4];
      let totalConectadas = 0;
      
      for (const numeroInstancia of instancias) {
        try {
          const status = await estaConectado(numeroInstancia);
          
          // Verificar se o WhatsApp está conectado (phone_connected = true)
          if (status && status.message && status.message.phone_connected === true) {
            totalConectadas++;
          }
        } catch (error) {
          console.warn(`Erro ao verificar instância ${numeroInstancia}:`, error);
        }
      }

      return totalConectadas;
    } catch (error) {
      console.error('Erro ao calcular instâncias conectadas:', error);
      return 0;
    }
  };

  const processarDadosGrafico = (mensagens, dataReferencia) => {
    const ano = dataReferencia.getFullYear();
    const mes = dataReferencia.getMonth();
    const diasNoMes = new Date(ano, mes + 1, 0).getDate();
    const hoje = dataReferencia.getDate();

    return Array.from({ length: diasNoMes }, (_, i) => {
      const dia = i + 1;
      const valor = dia <= hoje ? mensagens.filter(msg => {
        if (!msg.criadoEm) return false;
        const dataMensagem = new Date(msg.criadoEm);
        return dataMensagem.getDate() === dia && dataMensagem.getMonth() === mes && dataMensagem.getFullYear() === ano;
      }).length : 0;
      
      return { day: dia, value: valor, date: new Date(ano, mes, dia) };
    });
  };

  const processarAtividadesRecentes = (mensagens) => {
    return mensagens.slice(-4).reverse().map((msg, index) => ({
      id: msg.id || index,
      tipo: msg.tipo || 'Envio',
      contato: msg.contato?.nome || 'Contato não identificado',
      conteudo: (msg.texto || msg.textoSemFormatacao || 'Sem conteúdo').substring(0, 60) + (msg.texto?.length > 60 ? '...' : ''),
      hora: msg.criadoEm ? new Date(msg.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '--:--',
      status: 'enviada'
    }));
  };

  const calculateChartStats = (data) => {
    const validData = data.filter(d => d.value > 0);
    const total = validData.reduce((sum, d) => sum + d.value, 0);
    const today = data.find(d => d.day === new Date().getDate())?.value || 0;
    
    setChartStats({
      total,
      average: validData.length > 0 ? Math.round(total / validData.length) : 0,
      peak: Math.max(...validData.map(d => d.value), 0),
      today
    });
  };

  const drawChart = () => {
    const svg = chartRef.current;
    if (!svg || chartData.length === 0) return;

    try {
      const width = svg.clientWidth || 800;
      const height = 200;
      const padding = { top: 20, right: 30, bottom: 40, left: 50 };
      const chartWidth = width - padding.left - padding.right;
      const chartHeight = height - padding.top - padding.bottom;
      const maxValue = Math.max(...chartData.map(d => d.value)) || 100;
      const maxDay = Math.max(...chartData.map(d => d.day));

      svg.innerHTML = `<defs><linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" style="stop-color:#43a047;stop-opacity:0.3" /><stop offset="100%" style="stop-color:#43a047;stop-opacity:0.05" /></linearGradient></defs>`;

    // Grid e labels
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (chartHeight * i / 5);
      const gridLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      gridLine.setAttribute('x1', padding.left);
      gridLine.setAttribute('y1', y);
      gridLine.setAttribute('x2', width - padding.right);
      gridLine.setAttribute('y2', y);
      gridLine.setAttribute('stroke', '#f0f0f0');
      svg.appendChild(gridLine);

      const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      label.setAttribute('x', padding.left - 10);
      label.setAttribute('y', y + 4);
      label.setAttribute('text-anchor', 'end');
      label.setAttribute('font-size', '10');
      label.setAttribute('fill', '#666');
      label.textContent = Math.round(maxValue * (1 - i / 5));
      svg.appendChild(label);
    }

    // Linha e pontos
    let pathData = '';
    let areaData = '';
    const validData = chartData.filter(d => d.value > 0);

    validData.forEach((point, index) => {
      const x = padding.left + (chartWidth * (point.day - 1) / (maxDay - 1));
      const y = padding.top + chartHeight - (chartHeight * point.value / maxValue);

      if (index === 0) {
        pathData += `M ${x} ${y}`;
        areaData += `M ${x} ${padding.top + chartHeight} L ${x} ${y}`;
      } else {
        pathData += ` L ${x} ${y}`;
        areaData += ` L ${x} ${y}`;
      }

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', x);
      circle.setAttribute('cy', y);
      circle.setAttribute('r', 4);
      circle.setAttribute('fill', '#43a047');
      circle.setAttribute('stroke', 'white');
      circle.setAttribute('stroke-width', '2');
      circle.style.cursor = 'pointer';
      circle.addEventListener('mouseenter', (e) => showTooltip(e, point));
      circle.addEventListener('mouseleave', hideTooltip);
      svg.appendChild(circle);
    });

    // Área e linha
    if (validData.length > 0) {
      const lastPoint = validData[validData.length - 1];
      const lastX = padding.left + (chartWidth * (lastPoint.day - 1) / (maxDay - 1));
      areaData += ` L ${lastX} ${padding.top + chartHeight} Z`;

      const area = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      area.setAttribute('d', areaData);
      area.setAttribute('fill', 'url(#gradient)');
      svg.appendChild(area);

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', pathData);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', '#43a047');
      path.setAttribute('stroke-width', '3');
      path.setAttribute('stroke-linecap', 'round');
      svg.appendChild(path);
    }

    // Labels do eixo X
    [1, 5, 10, 15, 20, 25, maxDay].forEach(day => {
      if (day <= maxDay) {
        const x = padding.left + (chartWidth * (day - 1) / (maxDay - 1));
        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('x', x);
        label.setAttribute('y', height - 10);
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('font-size', '10');
        label.setAttribute('fill', '#666');
        label.textContent = day;
        svg.appendChild(label);
      }
    });
    } catch (error) {
      console.error('Erro ao desenhar gráfico:', error);
      // Fallback: mostrar mensagem simples
      svg.innerHTML = '<text x="50%" y="50%" text-anchor="middle" fill="#666" font-size="14">Gráfico temporariamente indisponível</text>';
    }
  };

  const showTooltip = (event, point) => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    tooltip.innerHTML = `<strong>Dia ${point.day}</strong><br>${point.date.toLocaleDateString('pt-BR')}<br><span style="color: #a5d6a7;">${point.value} envios</span>`;
    const rect = event.target.getBoundingClientRect();
    const containerRect = tooltip.parentElement.getBoundingClientRect();
    tooltip.style.left = (rect.left - containerRect.left) + 'px';
    tooltip.style.top = (rect.top - containerRect.top - 60) + 'px';
    tooltip.style.opacity = '1';
  };

  const hideTooltip = () => tooltipRef.current && (tooltipRef.current.style.opacity = '0');

  const getStatusColor = (status) => ({ 'enviada': '#2e7d32', 'pendente': '#ff9800', 'falhou': '#d32f2f' }[status] || '#6c757d');
  const getTipoColor = (tipo) => ({ 'Confirmação': '#43a047', 'Lembrete': '#ff9800', 'Aniversário': '#2196f3', 'Promocional': '#9c27b0' }[tipo] || '#6c757d');

  const quickActions = [
    { title: 'Mensagens', icon: '💬', description: 'Configurar mensagens', color: '#43a047' },
    { title: 'Ver Envios', icon: '📤', description: 'Histórico de mensagens enviadas', color: '#2e7d32' },
    { title: 'Automações', icon: '🤖', description: 'Configurar envios automáticos', color: '#388e3c' },
    { title: 'Gatilhos', icon: '⚡', description: 'Configurar disparos automáticos', color: '#43a047' },
    { title: 'Auto Atendimento', icon: '🧠', description: 'Configurar bot e respostas automáticas', color: '#2196f3' },
    { title: 'Configurações', icon: '⚙️', description: 'QR Code e conexões', color: '#4caf50' },
    { title: 'Ajuda', icon: '❓', description: 'Central de suporte e tutoriais', color: '#1976d2' },
  ];

  const statCards = [
    { value: stats.enviosHoje, label: 'Envios Hoje', icon: '📊', color: '#43a047' },
    { value: stats.mensagensEnviadas, label: 'Total de Mensagens', icon: '📨', color: '#2e7d32' },
    { value: stats.automacoesAtivas, label: 'Automações Ativas', icon: '🤖', color: '#388e3c' },
    { value: stats.instanciasConectadas, label: 'WhatsApp Conectado', icon: '📱', color: '#4caf50' }
  ];

  const getCurrentPeriod = () => {
    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const now = new Date();
    return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  };

  const handleWelcomeContinue = () => {
    setClinicaCadastrada(true);
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  };

  // Mostrar página de boas-vindas se clínica não estiver cadastrada
  if (clinicaCadastrada === false) {
    return <WelcomePage onContinue={handleWelcomeContinue} />;
  }

  // Mostrar loading enquanto verifica cadastro ou carrega dados
  if (clinicaCadastrada === null || stats.loading) {
    return (
      <div style={{backgroundColor: '#f8f9fa', minHeight: '100vh'}}>
        <div style={{backgroundColor: '#43a047', color: 'white', padding: '12px 20px', fontSize: '16px', fontWeight: '500'}}>gsConnect - Dashboard</div>
        <Container fluid className="p-4"><div className="text-center py-5"><Spinner animation="border" style={{color: '#43a047'}} /><p className="mt-3 text-muted">Carregando dashboard...</p></div></Container>
      </div>
    );
  }

  return (
    <div style={{backgroundColor: '#f8f9fa', minHeight: '100vh'}}>
      <div style={{backgroundColor: '#43a047', color: 'white', padding: '12px 20px', fontSize: '16px', fontWeight: '500'}}>gsConnect - Dashboard</div>

      <Container fluid className="p-4">
        {/* Status Cards */}
        <Row className="mb-4">
          {statCards.map((stat, index) => (
            <Col md={3} key={index} className="mb-3">
              <Card className="h-100 border-0 shadow-sm" style={{transition: 'all 0.2s ease'}}>
                <Card.Body className="text-center">
                  <div style={{fontSize: '2rem', color: stat.color, marginBottom: '8px'}}>{stat.icon}</div>
                  <h3 className="mb-1" style={{color: stat.color, fontWeight: '600'}}>{stat.value}</h3>
                  <small className="text-muted">{stat.label}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        

        {/* Gráfico */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body style={{padding: '20px'}}>
                <div className="d-flex justify-content-between align-items-center mb-3" style={{paddingBottom: '15px', borderBottom: '2px solid #e8f5e8'}}>
                  <h5 style={{fontSize: '16px', fontWeight: '600', color: '#2e7d32', margin: '0'}}>📈 Envios do Mês Atual</h5>
                  <span style={{fontSize: '12px', color: '#666', background: '#e8f5e8', padding: '4px 8px', borderRadius: '12px'}}>{getCurrentPeriod()}</span>
                </div>
                <div style={{position: 'relative'}}>
                  <svg ref={chartRef} style={{width: '100%', height: '200px', overflow: 'visible'}} />
                  <div ref={tooltipRef} style={{position: 'absolute', background: '#2e7d32', color: 'white', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', pointerEvents: 'none', opacity: '0', transition: 'opacity 0.2s ease', zIndex: '1000', boxShadow: '0 4px 12px rgba(0,0,0,0.2)'}} />
                </div>
                <div className="d-flex justify-content-around mt-3 pt-3" style={{borderTop: '1px solid #e8f5e8'}}>
                  {[
                    { label: 'Total do Mês', value: chartStats.total },
                    { label: 'Média Diária', value: chartStats.average },
                    { label: 'Pico Máximo', value: chartStats.peak },
                    { label: 'Hoje', value: chartStats.today }
                  ].map((stat, index) => (
                    <div key={index} className="text-center">
                      <h6 style={{fontSize: '18px', fontWeight: '600', color: '#2e7d32', margin: '0'}}>{stat.value}</h6>
                      <p style={{fontSize: '11px', color: '#666', margin: '2px 0 0 0'}}>{stat.label}</p>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Ações Rápidas */}
          <Col lg={8} className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Header style={{backgroundColor: '#f8f9fa', border: 'none', padding: '16px 20px'}}>
                <h5 className="mb-0" style={{color: '#495057', fontWeight: '600'}}>Ações Rápidas</h5>
              </Card.Header>
              <Card.Body style={{padding: '20px'}}>
                <Row className="g-1">
                  {quickActions.map((action, index) => {
                    // Definir o destino do link para cada ação
                    let linkTo = null;
                    if (action.title === 'Mensagens') linkTo = '/admin/mensagens';
                    if (action.title === 'Ver Envios') linkTo = '/admin/Envios';
                    if (action.title === 'Automações') linkTo = '/admin/automacao';
                    if (action.title === 'Gatilhos') linkTo = '/admin/gatilhos';
                    if (action.title === 'Auto Atendimento') linkTo = '/admin/autoatendimento';
                    if (action.title === 'Configurações') linkTo = '/admin/configuracoes';
                    if (action.title === 'Ajuda') linkTo = '/admin/Ajuda';
                    const CardContent = (
                      <Card className="border-0" style={{maxWidth: '260px', background: `linear-gradient(135deg, ${action.color}15 0%, ${action.color}05 100%)`, border: `1px solid ${action.color}30`, cursor: 'pointer', transition: 'all 0.2s ease'}} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                        <Card.Body className="py-2 px-2 d-flex align-items-start" style={{height: '100%', textAlign: 'left'}}>
                          <div style={{fontSize: '1.2rem', marginRight: '8px', width: '28px', height: '28px', backgroundColor: action.color, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>{action.icon}</div>
                          <div className="flex-grow-1" style={{textAlign: 'left'}}>
                            <h6 className="mb-1" style={{color: action.color, fontWeight: '600', fontSize: '0.98rem', textAlign: 'left'}}>{action.title}</h6>
                            <p className="mb-0 text-muted" style={{fontSize: '11px', textAlign: 'left'}}>{action.description}</p>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                    return (
                      <Col md={6} key={index} className="mb-1">
                        {linkTo ? (
                          <Link to={linkTo} style={{textDecoration: 'none'}}>
                            {CardContent}
                          </Link>
                        ) : CardContent}
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Últimas Enviadas */}
          <Col lg={4} className="mb-4">
            <Card className="border-0 shadow-sm h-100">
              <Card.Header style={{backgroundColor: '#f8f9fa', border: 'none', padding: '16px 20px'}}>
                <h5 className="mb-0" style={{color: '#495057', fontWeight: '600'}}>📨 Últimas Enviadas</h5>
              </Card.Header>
              <Card.Body style={{padding: '0'}}>
                {recentActivity.length > 0 ? (
                  <>
                    {recentActivity.map((activity, index) => (
                      <div key={activity.id} className="p-3" style={{borderBottom: index < recentActivity.length - 1 ? '1px solid #f0f0f0' : 'none', transition: 'background-color 0.2s ease'}} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}>
                        <div className="d-flex align-items-start">
                          <div style={{width: '8px', height: '8px', backgroundColor: getStatusColor(activity.status), borderRadius: '50%', marginTop: '6px', marginRight: '12px', flexShrink: 0}} />
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <span style={{fontSize: '12px', color: getTipoColor(activity.tipo), fontWeight: '500'}}>{activity.tipo}</span>
                              <span style={{fontSize: '11px', color: '#9e9e9e'}}>{activity.hora}</span>
                            </div>
                            <div style={{fontSize: '13px', color: '#495057', fontWeight: '500', marginBottom: '2px'}}>{activity.contato}</div>
                            <p className="mb-0" style={{fontSize: '12px', color: '#6c757d', lineHeight: '1.4'}}>{activity.conteudo}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="p-3 text-center">
                      <Button variant="outline-secondary" size="sm" style={{fontSize: '12px', padding: '6px 16px'}}>Ver Todas as Mensagens</Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <div style={{fontSize: '2rem', opacity: '0.3', marginBottom: '8px'}}>📭</div>
                    <p className="text-muted mb-0" style={{fontSize: '13px'}}>Nenhuma mensagem enviada hoje</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default DashboardView;